// PayOrderBase.tsx
import { useEffect, useRef, useState, ComponentType } from 'react';
import WebUniversal, { WebUniversalProps } from '../system/WebUniversal';
import { WebEditUniversalProps } from '../system/WebEditUniversal';
import { columnsheadId } from '../universal/columnshead';
import { Button, Space, Tooltip, Popover } from 'antd';
import AntdNotification from '../../utils/extend/AntdNotification';
import dayjs from 'dayjs';
import { LoggerTypes } from '../../bmCommonLibrary/bmDeclareTypes';
import { DeleteOptions, LoggerProps } from '../../utilsTsx/components/system/BaseWebUniversal';
import fetchApi from '../../utilsTsx/components/system/fetchApi';

export interface PayOrderBaseConfig {
    /** 业务 apiId，例如 'payOrder' / 'payOrderBak' */
    apiId: string;
    /** 微信订单查询使用的 apiId（一般跟 apiId 保持同后缀） */
    wxPayOrderApiId?: string;
    /** 表格标题 */
    titleName?: string;
    /** 三个子组件（由外层传入，避免基类直接 import 具体实现） */
    PayOrderEditComponent: ComponentType<any>;
    WxPayOrderEditComponent: ComponentType<any>;
    WxPayRefundOrderComponent: ComponentType<any>;
    /** 透传给 WebUniversal 的外部 props（即原来函数组件的 props） */
    outerProps?: any;
}

const PayOrderBase = (config: PayOrderBaseConfig) => {
    const {
        apiId,
        wxPayOrderApiId = 'wxPayOrder',
        titleName = '支付订单',
        PayOrderEditComponent,
        WxPayOrderEditComponent,
        WxPayRefundOrderComponent,
        outerProps,
    } = config;

    /** 可使用WebUniversal(父)组件函数 */
    const ref = useRef<any>(undefined);
    const [modalVisibleWxPayOrderEdit, setModalVisibleWxPayOrderEdit] = useState(false);
    const [modalVisibleWxPayRefundOrder, setModalVisibleWxPayRefundOrder] = useState(false);
    const [modalData, setModalData] = useState<WebEditUniversalProps>();
    const [activePopoverId, setActivePopoverId] = useState<string | null>(null);
    const [defaultService, setDefaultService] = useState<string>('');

    const onVisibleModalWxPayOrderEdit = (state: boolean) => setModalVisibleWxPayOrderEdit(state);
    const onVisibleModalWxPayRefundOrder = (state: boolean) => setModalVisibleWxPayRefundOrder(state);

    useEffect(() => {
        if (ref.current) {
            setDefaultService(ref.current.defaultService);
        }
    }, []);

    /** 微信订单查询 */
    const wxPayOrderQuery = async (id: string) => {
        fetchApi.GET(ref.current.defaultService, {
            query: {
                apiId: wxPayOrderApiId,
                apiExtend: 'orderQuery',
                apiVariable: id
            }
        }).then(res => {
            if (res.success === true) {
                ref.current.fetchData();
                AntdNotification('success', '查询', JSON.stringify(res.data));
            } else {
                ref.current.fetchData();
                AntdNotification('error', '查询', res.message);
            }
        });
    };

    /** 关闭付款 */
    const closePayment = async (id: string) => {
        fetchApi.PUT(defaultService, {
            body: {
                apiId,                          // ← 用变量替换硬编码
                apiExtend: 'closePayment',
                apiData: { payOrderId: id }
            }
        }).then(res => {
            if (res.success === true) {
                ref.current.fetchData();
                AntdNotification('success', '关闭付款成功');
            } else {
                AntdNotification('error', '关闭付款失败', '支付单已支付或已关闭');
            }
        });
    };

    /** 表头内容 */
    const columnsHead = [
        {
            title: '方式',
            dataIndex: 'payModeId',
            sorter: true,
            render: (val: any, record: any) => record?.payMode?.name
        },
        { title: '金额', dataIndex: 'payAmount' },
        {
            title: '进程',
            dataIndex: 'payProcess',
            render: (val: any) => (val?.id === 0 ? <p style={{ color: 'yellow' }}>{val?.name}</p> : val?.name)
        },
        {
            title: '支付',
            dataIndex: 'isPaySuccess',
            render: (val: boolean) => (val === false ? <p style={{ color: 'red' }}>失败</p> : '成功')
        },
        {
            title: '退款',
            dataIndex: 'refundProcess',
            sorter: true,
            render: (val: any) => (val?.id !== 0 ? <p style={{ color: 'red' }}>{val?.name}</p> : val?.name)
        },
        {
            title: '商户单',
            dataIndex: 'merchantOrderId',
            render: (text: any, record: any) => {
                const merchantId = record.merchantId;
                const merchantName = record.merchant?.merchantName;
                const merchantOrderType = record?.merchantOrderType?.orderTypeName;
                const titleStr = `商户id：${merchantId}，商户名称：${merchantName}，单类型：${merchantOrderType}，单号：${text}`;
                return <Tooltip placement="topLeft" title={titleStr}>{
                    (typeof text !== "undefined" && text !== null) ?
                        (text.length >= 8 ? text.substring(0, 8) + '......' : text) : ''
                }</Tooltip>;
            }
        },
        {
            title: '',
            dataIndex: 'id',
            render: (id: string) => (
                <Popover
                    title="是否关闭付款？"
                    trigger="click"
                    content={(<Space>
                        <Button onClick={() => setActivePopoverId(null)}>否</Button>
                        <Button type='primary' onClick={() => {
                            closePayment(id);
                            setActivePopoverId(null);
                        }}>是</Button>
                    </Space>)}
                    open={activePopoverId === id}
                    onOpenChange={(visible) => setActivePopoverId(visible ? id : null)}
                >
                    <Button type="default">关闭付款</Button>
                </Popover>
            )
        }
    ];

    const expandableColumns = [
        { title: '微信订单', dataIndex: 'id' },
        {
            title: '类型',
            dataIndex: 'tradeType',
            render: (val: any) => <Tooltip placement="topLeft" title={val?.describe}>{val?.name}</Tooltip>
        },
        {
            title: '',
            dataIndex: 'id',
            sorter: true,
            render: (val: string) => (
                <Button type='dashed' onClick={() => wxPayOrderQuery(val)}>{'查询'}</Button>
            )
        },
        {
            title: '微信来源',
            dataIndex: 'wxSource',
            render: (val: any) => <Tooltip placement="topLeft" title={`${val?.id} - ${val?.name}`}>{
                (typeof val?.id !== "undefined" && val?.id !== null) ?
                    (val?.id.length >= 8 ? val?.id.substring(0, 8) + '......' : val?.id) : ''
            }</Tooltip>
        },
        {
            title: '微信支付',
            dataIndex: 'wxPay',
            render: (val: any) => <Tooltip placement="topLeft" title={`${val?.id} - ${val?.name}`}>{
                (typeof val?.id !== "undefined" && val?.id !== null) ?
                    (val?.id.length >= 8 ? val?.id.substring(0, 8) + '......' : val?.id) : ''
            }</Tooltip>
        },
        { title: '金额', dataIndex: 'payAmount' },
        {
            title: '交易时间',
            dataIndex: 'tradeTime',
            render: (text: any) => dayjs(text).format('YYYY-MM-DD HH:mm:ss')
        },
        {
            title: '交易',
            dataIndex: 'isTransactionSuccessful',
            render: (val: boolean) => (val === false ? <p style={{ color: 'red' }}>失败</p> : '成功')
        },
        {
            title: '',
            dataIndex: 'refundProcess',
            render: (val: any, record: any) => (
                <Button type='primary' danger={val?.id === 0 ? false : true} onClick={() => {
                    setModalData({
                        apiId: '',
                        titleName: '',
                        editMode: 'EDIT',
                        editingId: record.id
                    });
                    onVisibleModalWxPayRefundOrder(true);
                }}>{val?.name}</Button>
            )
        },
        {
            title: '可付款',
            dataIndex: 'payableStatus',
            render: (val: any, record: any) => (
                <Space>
                    {record.isTransactionSuccessful === false ? (val?.name) : ''}
                    <> </>
                    <Button type="primary" onClick={() => {
                        setModalData({
                            apiId: '',
                            titleName: '',
                            editMode: 'EDIT',
                            editingId: record.id
                        });
                        setModalVisibleWxPayOrderEdit(true);
                    }}>{'编辑'}</Button>
                </Space>
            )
        }
    ];

    const columns = columnsheadId.concat(columnsHead as []);

    const extendProps: WebUniversalProps = {
        ...outerProps,
        titleName,
        serviceId: 'pay',
        apiId,
        columns,
        expandableOptions: {
            subColumns: expandableColumns,
            subDataName: 'wxPayOrders'
        },
        displayOptions: {
            showAdd: false,
            loggerProps: {
                showLogger: true,
                showSingleLogger: true,
                LoggerTypes,
                loggerTypeId: 'PAY',
                queryType: 'payOrder'
            } as LoggerProps<typeof LoggerTypes>
        },
        editOptions: {
            Editor: PayOrderEditComponent   // ← 由外部注入
        },
        onCallbacks: {
            onDelete: (optins: DeleteOptions) => {
                const { record } = optins;
                fetchApi.DELETE(defaultService, {
                    body: {
                        apiId,                    // ← 用变量替换硬编码
                        apiExtend: 'deletePayment',
                        apiVariable: record.id
                    }
                }).then(res => {
                    if (res.success === true) {
                        ref.current.fetchData();
                        AntdNotification('success', '删除支付订单成功');
                    } else {
                        AntdNotification('error', '删除支付订单失败');
                    }
                });
            }
        }
    };

    return (
        <>
            <WebUniversal {...extendProps} ref={ref} />
            {modalVisibleWxPayOrderEdit
                ? <WxPayOrderEditComponent
                    defaultService={defaultService}
                    closeModal={() => onVisibleModalWxPayOrderEdit(false)}
                    {...modalData} />
                : null}
            {modalVisibleWxPayRefundOrder
                ? <WxPayRefundOrderComponent
                    defaultService={defaultService}
                    closeModal={() => onVisibleModalWxPayRefundOrder(false)}
                    {...modalData} />
                : null}
        </>
    );
};

export default PayOrderBase;